"use client";

import { useEffect, useState } from "react";
import { logger } from "@/lib/log";
import { installErrorReporter } from "@/lib/error-reporter";
import {
  isChunkLoadError,
  shouldAttemptChunkReload,
  triggerChunkReload,
} from "@/lib/chunk-load-error";

const log = logger("app/global-error");

// global-error replaces the root layout when an error bubbles all the way up,
// so it must render its own <html> and <body>. Inline styles only — global
// CSS is not guaranteed to have loaded by the time this renders.
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [reloading] = useState(
    () => isChunkLoadError(error) && shouldAttemptChunkReload(),
  );

  useEffect(() => {
    // global-error replaces the root layout, so Providers (which installs the
    // reporter on mount) never ran — install it here too before logging, or
    // these root-level crashes stay console-only. Idempotent.
    installErrorReporter();
    if (reloading) {
      log.warn("chunk load error in global boundary; auto-reloading", {
        message: error.message,
      });
      triggerChunkReload();
      return;
    }
    log.error("global error boundary caught", {
      message: error.message,
      stack: error.stack,
      digest: error.digest,
    });
  }, [error, reloading]);

  if (reloading) {
    return (
      <html lang="en">
        <body
          style={{
            fontFamily: "system-ui, -apple-system, sans-serif",
            margin: 0,
            padding: "2rem",
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#fafafa",
            color: "#555",
            fontSize: "0.875rem",
          }}
        >
          Updating to the latest version…
        </body>
      </html>
    );
  }

  return (
    <html lang="en">
      <body
        style={{
          fontFamily: "system-ui, -apple-system, sans-serif",
          margin: 0,
          padding: "2rem",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fafafa",
          color: "#111",
        }}
      >
        <div style={{ maxWidth: 480, textAlign: "center" }}>
          <div style={{ fontSize: "2rem" }}>⚠️</div>
          <h2 style={{ fontSize: "1.125rem", fontWeight: 600, margin: "1rem 0 0.5rem" }}>
            Something went wrong
          </h2>
          <p style={{ fontSize: "0.875rem", color: "#555", wordBreak: "break-word", margin: 0 }}>
            {error.message || "An unexpected error occurred."}
          </p>
          {error.digest && (
            <p
              style={{
                fontSize: "0.625rem",
                color: "#999",
                fontFamily: "ui-monospace, monospace",
                marginTop: "0.75rem",
              }}
            >
              ref: {error.digest}
            </p>
          )}
          <div style={{ marginTop: "1.25rem", display: "flex", gap: "0.5rem", justifyContent: "center" }}>
            <button
              onClick={() => reset()}
              style={{
                fontSize: "0.75rem",
                padding: "0.375rem 0.75rem",
                borderRadius: 4,
                border: "1px solid #ccc",
                background: "white",
                cursor: "pointer",
              }}
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                fontSize: "0.75rem",
                padding: "0.375rem 0.75rem",
                borderRadius: 4,
                border: "1px solid #ccc",
                background: "white",
                cursor: "pointer",
              }}
            >
              Reload page
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
